import { SystemData } from '../../../../../types/systems';
import { FieldRules, rules, runRules, ValidatedJsonType, ValidationResult } from '../validator';

function sisoUidRules(value: unknown): { reason: string; check: () => boolean }[] {
	const asString = typeof value === 'string' ? value.trim() : '';
	const parts = asString ? asString.split('.') : [];
	const allDigits = parts.every((p) => /^\d+$/.test(p));
	const nums = allDigits ? parts.map(Number) : [];
	const uint8Indices = [0, 1, 3, 4, 5, 6];

	return [
		{
			reason: 'must be a non-empty string',
			check: () => typeof value === 'string' && !!value.trim(),
		},
		{
			reason: 'must contain 4 to 7 dot-separated parts',
			check: () => parts.length >= 4 && parts.length <= 7,
		},
		{ reason: 'every part must be an integer', check: () => parts.length > 0 && allDigits },
		{
			reason: 'octets at positions 0,1,3,4,5,6 must be <= 255',
			check: () =>
				allDigits && uint8Indices.every((i) => nums[i] === undefined || nums[i] <= 255),
		},
		{
			reason: 'third part (position 2) must be <= 65535',
			check: () => allDigits && (nums[2] === undefined || nums[2] <= 65535),
		},
	];
}

export function isSystemJson(value: ValidatedJsonType): ValidationResult {
	const json = value as SystemData;

	const fields: FieldRules[] = [
		{ key: 'name', actual: json.name, rules: [rules.isString(json.name)] },
		{ key: 'symbol', actual: json.symbol, rules: [rules.isString(json.symbol)] },
		{ key: 'side', actual: json.side, rules: [rules.isNumber(json.side)] },
		{ key: 'platform', actual: json.platform, rules: [rules.isNumber(json.platform)] },
		{ key: 'armor', actual: json.armor, rules: [rules.isString(json.armor)] },
		{ key: 'id', actual: json.id, rules: [rules.optional(json.id, rules.isNumber(json.id))] },
		{ key: 'icon', actual: json.icon, rules: [rules.isString(json.icon)] },
		{
			key: 'cost',
			actual: json.cost,
			rules: [rules.isString(json.cost), rules.noSpaces(json.cost)],
		},
		{
			key: 'take_off_time',
			actual: json.take_off_time,
			rules: [rules.optional(json.take_off_time, rules.isNumber(json.take_off_time))],
		},
		{
			key: 'time_to_ready_to_fire',
			actual: json.time_to_ready_to_fire,
			rules: [
				rules.optional(
					json.time_to_ready_to_fire,
					rules.isNumber(json.time_to_ready_to_fire)
				),
			],
		},
		{
			key: 'dismount_time',
			actual: json.dismount_time,
			rules: [rules.optional(json.dismount_time, rules.isNumber(json.dismount_time))],
		},
		{
			key: 'blue_subordinate_behavior',
			actual: json.blue_subordinate_behavior,
			rules: [
				rules.isString(json.blue_subordinate_behavior),
				rules.notBlank(json.blue_subordinate_behavior),
			],
		},
		{
			key: 'red_subordinate_behavior',
			actual: json.red_subordinate_behavior,
			rules: [
				rules.isString(json.red_subordinate_behavior),
				rules.notBlank(json.red_subordinate_behavior)
			],
		},
		{
			key: 'blue_commander_behavior',
			actual: json.blue_commander_behavior,
			rules: [
				rules.isString(json.blue_commander_behavior),
				rules.notBlank(json.blue_commander_behavior)
			],
		},
		{
			key: 'red_commander_behavior',
			actual: json.red_commander_behavior,
			rules: [
				rules.isString(json.red_commander_behavior),
				rules.notBlank(json.red_commander_behavior)
			],
		},
		{
			key: 'roles',
			actual: json.roles,
			rules: [
				rules.isArray(json.roles),
				rules.everyItem(
					json.roles,
					'every role must be a string',
					(r) => typeof r === 'string'
				),
			],
		},
		{
			key: 'sensors',
			actual: json.sensors,
			rules: [
				rules.isArray(json.sensors),
				rules.everyItem(
					json.sensors,
					'every sensor must be a number',
					(s) => typeof s === 'number'
				),
				rules.notEmpty(json.sensors),
			],
		},
		{
			key: 'emitters',
			actual: json.emitters,
			rules: [
				rules.isArray(json.emitters),
				rules.everyItem(
					json.emitters,
					'every emitter must be a number',
					(e) => typeof e === 'number'
				),
			],
		},
		{
			key: 'effectors',
			actual: json.effectors,
			rules: [
				rules.isArray(json.effectors),
				rules.everyItem(
					json.effectors,
					'every effector must have numeric effector_id, min_elevation, max_elevation, units_of_fire',
					(e: any) =>
						typeof e.effector_id === 'number' &&
						typeof e.min_elevation === 'number' &&
						typeof e.max_elevation === 'number' &&
						typeof e.units_of_fire === 'number'
				),
			],
		},
		{
			key: 'dismounts',
			actual: json.dismounts,
			rules: [
				rules.isArray(json.dismounts),
				rules.everyItem(
					json.dismounts,
					'every dismount must be a number',
					(d) => typeof d === 'number'
				),
			],
		},
		{
			key: 'siso_uid',
			actual: json.siso_uid,
			rules: sisoUidRules(json.siso_uid),
		},
	];

	return runRules(fields);
}

export const validateSystem = (data: ValidatedJsonType): ValidationResult => {
	return isSystemJson(data);
};
